import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { FaTimes } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import { apiErrorMessage, selectPortalProps } from './documentHelpers';

export default function DocumentEditModal({ file, types, baseUrl, onClose, onSaved }) {
    const { token } = useAuth();
    const [name, setName] = useState('');
    const [typeId, setTypeId] = useState(null);
    const [expiryDate, setExpiryDate] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!file) return;
        setName(file.name || '');
        setTypeId(file.document_type_id || null);
        setExpiryDate(file.expiry_date || '');
        setNotes(file.notes || '');
        setError('');
    }, [file]);

    if (!file) return null;

    const typeOptions = types
        .filter(t => t.active !== false || t.id === file.document_type_id)
        .map(t => ({ value: t.id, label: t.active === false ? `${t.name} (inactive)` : t.name, hasExpiry: t.has_expiry }));
    const selectedType = typeOptions.find(o => o.value === typeId) || null;
    const showExpiry = Boolean(selectedType?.hasExpiry);

    const handleSave = async () => {
        if (!name.trim()) {
            setError('File name cannot be empty.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await axios.patch(
                `${baseUrl}/files/${encodeURIComponent(file.id)}`,
                { name: name.trim(), document_type_id: typeId, expiry_date: showExpiry ? expiryDate || null : null, notes: notes.trim() || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            onSaved();
        } catch (err) {
            setError(apiErrorMessage(err, 'Could not save the document. Please try again.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => !saving && onClose()}>
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-800">Edit document</h3>
                    <button type="button" onClick={onClose} disabled={saving} className="text-gray-400 hover:text-gray-600" aria-label="Close">
                        <FaTimes />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="doc-name" className="mb-1 block text-sm font-medium text-gray-700">File name</label>
                        <input
                            id="doc-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#FF5733] focus:outline-none"
                        />
                    </div>
                    <div>
                        <label htmlFor="doc-type" className="mb-1 block text-sm font-medium text-gray-700">Document type</label>
                        <Select
                            inputId="doc-type"
                            value={selectedType}
                            onChange={(option) => setTypeId(option ? option.value : null)}
                            options={typeOptions}
                            isClearable
                            placeholder="Untagged"
                            {...selectPortalProps}
                        />
                    </div>
                    {showExpiry && (
                        <div>
                            <label htmlFor="doc-expiry" className="mb-1 block text-sm font-medium text-gray-700">Expiry date</label>
                            <input
                                id="doc-expiry"
                                type="date"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#FF5733] focus:outline-none"
                            />
                        </div>
                    )}
                    <div>
                        <label htmlFor="doc-notes" className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                            id="doc-notes"
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#FF5733] focus:outline-none"
                        />
                    </div>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving} className="rounded-sm border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSave} disabled={saving} className="rounded-sm bg-[#FF5733] px-4 py-2 text-sm font-medium text-white hover:bg-[#ff4520] disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
